import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  booleanAttribute,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type displayModeType = 'block' | 'absolute';

@Component({
  selector: 'glxy-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageComponent implements OnDestroy, OnInit {
  @HostBinding('class.display-mode-absolute')
  get isDisplayModeAbsolute(): boolean {
    return this.displayMode === 'absolute';
  }

  constructor(private readonly breakpointObserver: BreakpointObserver) {}

  @HostBinding('class') class = 'glxy-page';

  @Input() displayMode: displayModeType = 'block';

  @Input({ transform: booleanAttribute }) isLoading = false;

  @Input() mobileMaxWidth = 768;

  @Input() scrolledToBottomOffset = 0;

  @HostBinding('class.has-padding')
  @Input()
  pagePadding = true;

  @HostBinding('class.scroll-shadow')
  scrollShadow = false;

  @HostBinding('class.page-is-mobile')
  pageMobile = false;

  @Output() pageScroll: EventEmitter<Event> = new EventEmitter();

  @Output() pageScrolledToBottom: EventEmitter<null> = new EventEmitter();

  destroy$: Subject<void> = new Subject();

  @HostListener('window:scroll', ['$event'])
  windowScrollEvent($event: Event): void {
    this.onWindowScroll($event);
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe('(max-width: ' + this.mobileMaxWidth + 'px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.pageMobile = resp.matches;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageScroll(event: Event): void {
    // On desktop, we use the page's internal scroll
    if (this.pageMobile === false) {
      this.pageScroll.emit(event);

      const target = event.target as HTMLDivElement;
      if (!target) return;

      const { scrollTop, scrollHeight, offsetHeight } = target;

      // Hide and show shadow
      if (scrollTop > 0) {
        this.scrollShadow = true;
      } else {
        this.scrollShadow = false;
      }

      // Emit event when we scroll to bottom of page
      const scrollPosition = offsetHeight + scrollTop;
      const bottomScrollPosition = scrollHeight - this.scrolledToBottomOffset;

      if (scrollPosition >= bottomScrollPosition) {
        this.pageScrolledToBottom.emit();
      }
    }
  }

  onWindowScroll(event: Event): void {
    // On Mobile, use the Window Scroll
    if (this.pageMobile) {
      this.pageScroll.emit(event);

      // Scroll to Bottom Emitter
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - this.scrolledToBottomOffset) {
        this.pageScrolledToBottom.emit();
      }
    }
  }
}
